import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Sparkles, Check, RefreshCw } from 'lucide-react';
import { smartCopy } from '../../../utils/textFormatting';

/**
 * CaptionGenerator - Modal for AI caption generation
 * Allows selection of platform, tone, and voice for caption customization
 */
const CaptionGenerator = ({ clip, isOpen, onClose, onUseCaption }) => {
    const [platform, setPlatform] = useState('instagram');
    const [tone, setTone] = useState('interesting');
    const [voice, setVoice] = useState('first');
    const [generatedCaption, setGeneratedCaption] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);

    const platforms = [
        { id: 'instagram', name: 'Instagram', maxChars: 2200, emoji: '📸' },
        { id: 'tiktok', name: 'TikTok', maxChars: 2200, emoji: '🎵' },
        { id: 'youtube', name: 'YouTube', maxChars: 5000, emoji: '▶️' },
        { id: 'linkedin', name: 'LinkedIn', maxChars: 3000, emoji: '💼' },
        { id: 'twitter', name: 'Twitter/X', maxChars: 280, emoji: '🐦' },
        { id: 'facebook', name: 'Facebook', maxChars: 63206, emoji: '👥' }
    ];

    const tones = [
        { id: 'neutral', label: 'Neutral' },
        { id: 'interesting', label: 'Interesting' },
        { id: 'catchy', label: 'Catchy' },
        { id: 'serious', label: 'Serious' },
        { id: 'question', label: 'Question' }
    ];

    const voices = [
        { id: 'first', label: 'First Person (I, we)' },
        { id: 'third', label: 'Third Person (They, it)' }
    ];

    // Mock caption generation (replace with actual API call)
    const generateCaption = async () => {
        setIsGenerating(true);

        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));

        const selectedPlatform = platforms.find(p => p.id === platform);
        const mockCaption = `${selectedPlatform.emoji} Check out this amazing moment from our latest episode! 

${clip.title}

What do you think about this? Drop your thoughts below! 👇

#podcast #viral #content #${platform}`;

        setGeneratedCaption(mockCaption);
        setIsGenerating(false);
        setIsEditing(false);
    };

    const handleCopy = async () => {
        try {
            await smartCopy(generatedCaption, platform);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleUseCaption = () => {
        onUseCaption(generatedCaption, platform);
        onClose();
    };

    const selectedPlatform = platforms.find(p => p.id === platform);
    const charCount = generatedCaption.length;
    const charsRemaining = selectedPlatform ? selectedPlatform.maxChars - charCount : 0;

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-900">Generate AI Caption</h2>
                                <p className="text-sm text-slate-600">Platform-optimized captions</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-slate-600" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Platform Selection */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-3">
                                Platform
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                {platforms.map((p) => (
                                    <button
                                        key={p.id}
                                        onClick={() => setPlatform(p.id)}
                                        className={`px-3 py-2.5 rounded-xl font-medium text-sm transition-all ${platform === p.id
                                            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                            }`}
                                    >
                                        {p.emoji} {p.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Tone Selection */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-3">
                                Tone
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {tones.map((t) => (
                                    <button
                                        key={t.id}
                                        onClick={() => setTone(t.id)}
                                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${tone === t.id
                                            ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500'
                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-2 border-transparent'
                                            }`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Voice Selection */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-3">
                                Voice
                            </label>
                            <div className="flex gap-2">
                                {voices.map((v) => (
                                    <button
                                        key={v.id}
                                        onClick={() => setVoice(v.id)}
                                        className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all ${voice === v.id
                                            ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500'
                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border-2 border-transparent'
                                            }`}
                                    >
                                        {v.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Generate Button */}
                        {!generatedCaption && (
                            <button
                                onClick={generateCaption}
                                disabled={isGenerating}
                                className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isGenerating ? (
                                    <>
                                        <RefreshCw className="w-5 h-5 animate-spin" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-5 h-5" />
                                        Generate Caption
                                    </>
                                )}
                            </button>
                        )}

                        {/* Generated Caption Preview */}
                        {generatedCaption && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-semibold text-slate-700">
                                        Generated Caption
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setIsEditing(!isEditing)}
                                            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                                        >
                                            {isEditing ? 'Preview' : 'Edit'}
                                        </button>
                                        <button
                                            onClick={generateCaption}
                                            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                                        >
                                            <RefreshCw className="w-3 h-3" />
                                            Regenerate
                                        </button>
                                    </div>
                                </div>

                                {isEditing ? (
                                    <textarea
                                        value={generatedCaption}
                                        onChange={(e) => setGeneratedCaption(e.target.value)}
                                        className="w-full h-48 px-4 py-3 border border-slate-300 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none resize-none"
                                    />
                                ) : (
                                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 min-h-[120px] whitespace-pre-wrap">
                                        {generatedCaption}
                                    </div>
                                )}

                                <div className="flex items-center justify-between text-sm">
                                    <span className={`font-medium ${charsRemaining < 50 ? 'text-red-600' : 'text-slate-600'
                                        }`}>
                                        {charCount} / {selectedPlatform.maxChars} characters
                                    </span>
                                    {charsRemaining < 0 && (
                                        <span className="text-red-600 font-semibold">
                                            Over limit by {Math.abs(charsRemaining)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {generatedCaption && (
                        <div className="px-6 py-4 border-t border-slate-200 flex gap-3">
                            <button
                                onClick={handleCopy}
                                className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-4 h-4" />
                                        Copy
                                    </>
                                )}
                            </button>
                            <button
                                onClick={handleUseCaption}
                                disabled={charsRemaining < 0}
                                className="flex-1 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                Use for Publishing
                            </button>
                        </div>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default CaptionGenerator;
